/**
 * Section Preview Card Component
 *
 * Displays a single template section using the same styling as note sections.
 * Shows only the example output (markdown) in read-only mode.
 * Includes numbering and collapse/expand functionality.
 */

import React, { useState } from 'react';
import {
  TemplateSectionCard,
  TemplateSectionContent,
  TemplateSectionHeader,
} from '@components/TemplateSections';

import type { DraftSection } from '../types';

interface SectionPreviewCardProps {
  /** Section data to display */
  section: DraftSection;
  /** Whether this section is selected */
  isSelected?: boolean;
  /** Callback when section is clicked */
  onClick?: () => void;
  /** Section index for display */
  index: number;
}

/**
 * Section Preview Card - Shows a template section matching note section styling
 * with numbering and collapse/expand functionality
 */
export const SectionPreviewCard: React.FC<SectionPreviewCardProps> = ({
  section,
  isSelected = false,
  onClick,
  index,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const hasExample = Boolean(section.exampleOutput && section.exampleOutput.trim().length > 0);

  const handleSectionClick = (e: React.MouseEvent) => {
    // Only trigger onClick if clicking outside the header
    if (!(e.target as HTMLElement).closest('[data-section-header]')) {
      onClick?.();
    }
  };

  return (
    <TemplateSectionCard
      variant="note-style"
      isSelected={isSelected}
      onClick={handleSectionClick}
    >
      <TemplateSectionHeader
        title={section.title}
        index={index}
        isSelected={isSelected}
        collapsible={hasExample}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
      />
      <TemplateSectionContent
        markdown={section.exampleOutput}
        isLoading={!hasExample}
        loadingMessage="Generating preview..."
        collapsed={!isExpanded}
        isExample={true}
      />
    </TemplateSectionCard>
  );
};

